import React from 'react';
import {
  ArrowLeft, Receipt, FileText, BadgeCheck, Building2, Wallet, Info,
} from 'lucide-react';
import { useLanguage } from '../../components/LanguageContext';
import '../shared/portals.css';

// ============================================================
// PartnerPitch — the case for becoming a Cooperation Partner.
//
// It exists because the Partner entry is no longer reached from inside the
// owner menu, where everyone who saw it was already a customer. On the
// welcome screen it is seen by brokers and advisors who have never heard the
// offer, and dropping them straight onto an application form asks them to
// fill in a page before anyone has told them what they get.
//
// Editorial ink-and-gold, reusing the sc-* system the pre-login "What we
// offer" page already established, so the two marketing surfaces read as one
// site rather than two designs.
//
// Every claim here is something the product actually does — the commission
// statement fields come from Backend/cooperationRoutes (items, total,
// currency, status, file, payment reference) and the status name is the one
// the approval writes. Referral is honestly flagged as not open yet, because
// components/PartnerView still renders "coming soon" for it.
// ============================================================

// The skyline plate the services showcase uses for its hero. Applied as an
// inline background so a missing file degrades to the gradient rather than a
// torn image box.
const HERO_IMAGE = '/showcase-hero.jpg';

interface Props {
  /** Primary call to action — apply to the programme. */
  onApply: () => void;
  /**
   * Secondary route for someone who has already signed their agreement and
   * just wants back in. Without it, a returning partner's only visible action
   * would be to apply a second time.
   */
  onSignIn?: () => void;
  /** Back out of the Partner area entirely. Standalone (pre-login) only. */
  onBack?: () => void;
  /**
   * Rendered as a tab inside the signed-in portal rather than as its own
   * page: drop the top bar and stop the hero eating a whole viewport, since
   * a portal header and bottom nav are already on screen.
   */
  embedded?: boolean;
  /** Label for the primary button — differs before and after signing in. */
  ctaKey?: string;
}

const BENEFITS: { icon: React.ComponentType<any>; key: string }[] = [
  { icon: Wallet, key: 'pnBenefit1' },
  { icon: Receipt, key: 'pnBenefit2' },
  { icon: FileText, key: 'pnBenefit3' },
  { icon: BadgeCheck, key: 'pnBenefit4' },
  { icon: Building2, key: 'pnBenefit5' },
];

const STEPS = ['pnStep1', 'pnStep2', 'pnStep3', 'pnStep4'];

const PartnerPitch: React.FC<Props> = ({ onApply, onSignIn, onBack, embedded, ctaKey = 'pnCtaApply' }) => {
  const { t, language, setLanguage } = useLanguage();

  return (
    <div className="sc-screen" style={embedded ? { minHeight: 'auto' } : undefined}>
      {!embedded && (
        <div className="sc-topbar">
          <div className="sc-nav">
            {onBack && (
              <button className="sc-back" onClick={onBack}>
                <ArrowLeft size={15} strokeWidth={2} />
                <span>{t('scBack')}</span>
              </button>
            )}
            <img src="/asset-desk-wordmark.png" alt="Asset Desk" className="sc-logo" />
            <div className="sc-lang" role="group" aria-label={t('scLanguage')}>
              {(['en', 'de'] as const).map((code) => (
                <button
                  key={code}
                  type="button"
                  lang={code}
                  className={language === code ? 'is-on' : undefined}
                  aria-pressed={language === code}
                  onClick={() => setLanguage(code)}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <section className="sc-hero" style={embedded ? { minHeight: 400 } : undefined}>
        <div className="sc-hero-plate" style={{ backgroundImage: `url(${HERO_IMAGE})` }} />
        <div className="sc-hero-inner">
          <p className="sc-eyebrow">{t('pnPitchEyebrow')}</p>
          <h1 className="sc-hero-title">
            {t('pnPitchTitle')}
            <em>{t('pnPitchTitleEm')}</em>
          </h1>
          <p className="sc-hero-sub">{t('pnPitchSub')}</p>

          <div className="sc-hero-actions">
            <button className="sc-cta sc-cta--primary" onClick={onApply}>
              {t(ctaKey)}
            </button>
            {onSignIn && (
              <button className="sc-cta" onClick={onSignIn}>{t('rfAlreadyPartner')}</button>
            )}
          </div>

          <div className="sc-stats">
            <div className="sc-stat">
              <b>10</b>
              <span>{t('pnStatDevelopers')}</span>
            </div>
            <i aria-hidden="true" />
            <div className="sc-stat">
              <b>0</b>
              <span>{t('pnStatCost')}</span>
            </div>
            <i aria-hidden="true" />
            <div className="sc-stat">
              <b>4</b>
              <span>{t('pnStatSteps')}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="sc-wrap">
        <section className="sc-section">
          <p className="sc-sec-eyebrow">{t('pnBenefitsEyebrow')}</p>
          <h2 className="sc-sec-title">{t('pnBenefitsTitle')}</h2>
          <p className="sc-lede">{t('pnBenefitsLede')}</p>

          <ul className="sc-points">
            {BENEFITS.map(({ icon: Icon, key }) => (
              <li key={key}>
                <span className="sc-tile sc-tile--sm"><Icon size={16} strokeWidth={2} /></span>
                <span>{t(key)}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="sc-section">
          <p className="sc-sec-eyebrow">{t('pnStepsEyebrow')}</p>
          <h2 className="sc-sec-title">{t('pnStepsTitle')}</h2>

          <ul className="sc-steps">
            {STEPS.map((key, i) => (
              <li key={key}>
                <span className="sc-step-n">{i + 1}</span>
                <span className="sc-step-text">
                  <b>{t(`${key}Title`)}</b>
                  <span>{t(key)}</span>
                </span>
              </li>
            ))}
          </ul>

          {/* What we deliberately do NOT state here: the commission rate. It
              lives in the signed agreement, which differs per partner — a
              number on this page would be a promise the contract may not make. */}
          <div className="sc-flag-note">
            <Info size={15} strokeWidth={2} />
            <div>
              <b>{t('rfNoteTitle')}</b>
              <p>{t('rfNote')}</p>
            </div>
          </div>
        </section>
      </div>

      <section className="sc-closing">
        <div className="sc-wrap">
          <h2 className="sc-closing-title">{t('pnClosingTitle')}</h2>
          <p className="sc-lede">{t('pnClosingLede')}</p>
          <div className="sc-actions">
            <button className="sc-cta sc-cta--primary" onClick={onApply}>
              {t(ctaKey)}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerPitch;
